// src/components/sourcex/FieldTypePalette.jsx
//
// SourceX template editor — the left-rail "answer type" palette.
//
// Replaces the old flow (click Add, then pick a type from a plain <select>,
// then hand-type Table columns as "Label:Type, Label:Type") with a visual
// list of answer types a coordinator DRAGS onto an open question builder.
// Dragging in more than one type builds a composite question (see
// isCompositeTable in templateSchema.js).
//
// Drag-only, deliberately — this rail has no idea which question (if any) a
// click should apply to, and guessing "the last one" surprised people more
// than it helped. Once a question builder is open, the SAME types are also
// offered there as click-to-add chips, scoped to that one question.
//
// `Header` is deliberately excluded — it's a section title, not an answer
// type, and stays reachable through the existing "Add Heading" button.

import {
  Type,
  Hash,
  Calendar,
  CalendarClock,
  Clock,
  CircleHelp,
  CheckSquare,
  CircleDot,
  AlignLeft,
  ListChecks,
  Table2,
  PenLine,
} from "lucide-react";
import { FIELD_TYPES } from "../../services/templateSchema";

// The drag payload MIME type for a palette entry, carrying the field type
// string. A dedicated type (distinct from the outline's row-reorder drag and
// from the "Use from template" block drag) so a drop target can tell exactly
// what's being dragged over it without guessing from shape.
export const FIELD_TYPE_DRAG = "application/x-sourcex-fieldtype";

export const isFieldTypeDrag = (ev) => {
  const types = ev?.dataTransfer?.types;
  if (!types) return false;
  return Array.from(types).includes(FIELD_TYPE_DRAG);
};

export const readFieldTypeDrag = (ev) => {
  try {
    return ev.dataTransfer.getData(FIELD_TYPE_DRAG) || "";
  } catch {
    return "";
  }
};

// Single source of truth for a type's display name — shared with
// QuestionBuilder.jsx so the palette and the builder never call the same
// type two different things (this used to say "Select" here and "Dropdown"
// there).
export const FIELD_TYPE_LABELS = {
  Text: "Text",
  Number: "Number",
  Date: "Date",
  Time: "Time",
  DateTime: "Date & Time",
  "YesNo/Maybe": "Yes / No / N/A",
  Checkbox: "Checkbox",
  Select: "Dropdown",
  TextArea: "Long text",
  // "Checklist" was the one type nobody found, because nothing about the word
  // says it's the multi-answer twin of Dropdown. The two are now named and
  // listed as a pair (see PALETTE_TYPES) so the choice between them is made
  // once, in one place, instead of being discovered later.
  MultiSelect: "Multi Select",
  Table: "Table",
  Signature: "Signature block",
};

// One line explaining what each type actually IS — mainly to make the
// checkbox/dropdown/checklist distinction obvious at a glance, since "which
// of these three do I want" is the one question people kept getting stuck on.
export const FIELD_TYPE_HINTS = {
  Text: "A single-line write-in box",
  Number: "A numeric write-in box, with an optional unit",
  Date: "A date to fill in",
  Time: "A time to fill in",
  DateTime: "A date and time to fill in",
  "YesNo/Maybe": "A fixed Yes / No / N/A choice",
  Checkbox: "One yes/no tick box — no list of choices",
  // "○ / ☐" in the wording is deliberate: it is the same mark the printed
  // form uses, so the difference between these two is visible before the
  // choice is made, not only afterwards. See choiceMarkFor.
  Select: "Pick ONE — prints ○ circles",
  TextArea: "A multi-line write-in box",
  MultiSelect: "Tick ANY number — prints ☐ boxes",
  Table: "A repeatable log — rows, each with its own columns",
  Signature: "A signature + date line",
};

// Icon + accent per type, so the rail reads at a glance rather than by label
// alone. Colors are decorative only — every entry still works with a screen
// reader via its visible label.
const META = {
  Text: { icon: Type, color: "text-sky-300 bg-sky-400/10 border-sky-400/25" },
  Number: { icon: Hash, color: "text-emerald-300 bg-emerald-400/10 border-emerald-400/25" },
  Date: { icon: Calendar, color: "text-amber-300 bg-amber-400/10 border-amber-400/25" },
  Time: { icon: Clock, color: "text-amber-300 bg-amber-400/10 border-amber-400/25" },
  DateTime: { icon: CalendarClock, color: "text-amber-300 bg-amber-400/10 border-amber-400/25" },
  "YesNo/Maybe": { icon: CircleHelp, color: "text-purple-300 bg-purple-400/10 border-purple-400/25" },
  Checkbox: { icon: CheckSquare, color: "text-purple-300 bg-purple-400/10 border-purple-400/25" },
  // A circle icon for Dropdown and a checklist icon for Multi Select, matching
  // the ○ / ☐ marks each one prints — so the pair is distinguishable in the
  // rail at a glance, not only by reading both labels.
  Select: { icon: CircleDot, color: "text-purple-300 bg-purple-400/10 border-purple-400/25" },
  TextArea: { icon: AlignLeft, color: "text-sky-300 bg-sky-400/10 border-sky-400/25" },
  MultiSelect: { icon: ListChecks, color: "text-purple-300 bg-purple-400/10 border-purple-400/25" },
  Table: { icon: Table2, color: "text-rose-300 bg-rose-400/10 border-rose-400/25" },
  Signature: { icon: PenLine, color: "text-slate-300 bg-white/10 border-white/20" },
};

// Explicit order, not FIELD_TYPES' — that array is the schema's list and
// reordering it would reorder unrelated things. Here the order is a teaching
// order: write-in types, then the three "pick something" types with Dropdown
// and Multi Select adjacent so their difference is a side-by-side comparison,
// then the structural ones. Any type missing from this list is appended, so a
// new field type still shows up without being forgotten.
const PALETTE_ORDER = [
  "Text",
  "TextArea",
  "Number",
  "Date",
  "Time",
  "DateTime",
  "Select",
  "MultiSelect",
  "YesNo/Maybe",
  "Checkbox",
  "Table",
  "Signature",
];

export const PALETTE_TYPES = [
  ...PALETTE_ORDER.filter((t) => FIELD_TYPES.includes(t)),
  ...FIELD_TYPES.filter((t) => t !== "Header" && !PALETTE_ORDER.includes(t)),
];

export default function FieldTypePalette() {
  return (
    // Not sticky itself — the rail it sits in is, so the tools above it stay
    // pinned alongside rather than each box pinning separately.
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-3">
      <div className="px-1 pb-2 mb-2 border-b border-white/10">
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-white">
          Answer types
        </div>
        <p className="mt-1 text-[11px] text-slate-500">
          Open a question (Add field), then drag one of these onto it.
        </p>
      </div>
      <div className="flex flex-col gap-1">
        {PALETTE_TYPES.map((type) => {
          const { icon: Icon, color } = META[type] || META.Text;
          const label = FIELD_TYPE_LABELS[type] || type;
          return (
            <div
              key={type}
              draggable
              onDragStart={(ev) => {
                ev.dataTransfer.effectAllowed = "copy";
                try {
                  ev.dataTransfer.setData(FIELD_TYPE_DRAG, type);
                  ev.dataTransfer.setData("text/plain", type);
                } catch {
                  /* some browsers restrict setData outside a user gesture */
                }
              }}
              title={FIELD_TYPE_HINTS[type] || label}
              className="group flex items-center gap-2 px-2.5 py-2 rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.07] hover:border-white/20 cursor-grab active:cursor-grabbing transition text-left"
            >
              <span
                className={`shrink-0 w-7 h-7 rounded-lg border flex items-center justify-center ${color}`}
              >
                <Icon className="w-3.5 h-3.5" />
              </span>
              <span className="min-w-0">
                <span className="block text-xs font-medium text-slate-200 group-hover:text-white transition">
                  {label}
                </span>
                <span className="block text-[10px] text-slate-500 leading-tight truncate group-hover:text-slate-400">
                  {FIELD_TYPE_HINTS[type]}
                </span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
